/* eslint-disable max-lines */
import { QuickResolveActionItem, Situation } from '@hakimo-ui/hakimo/types';
import { useEffect, useReducer, useRef } from 'react';
import { SOPTabId } from './sop-card-utils';
import { useSOPCardState } from './SOPCardStateContext';

export enum SOPV2Step {
  OVERVIEW = 'overview',
  ESCALATE_DESCRIPTION = 'escalate_description',
  LIVE_TALKDOWN = 'live_talkdown',
  ESCALATION_ACTIONS = 'escalation_actions',
  RESOLVE = 'resolve',
}

export interface SOPV2State {
  step: SOPV2Step;
  selectedSituation?: Situation;
  selectedQuickResolveAction?: QuickResolveActionItem;
  escalationCommitted: boolean;
  talkdownStepsDone: number;
  pendingEscalation?: { hasTalkdown: boolean };
}

export type SOPV2Action =
  | { type: 'ALREADY_ESCALATED' }
  | { type: 'ESCALATION_GONE' }
  | { type: 'ESCALATE_CLICKED' }
  | { type: 'SITUATION_SELECTED'; situation: Situation }
  | { type: 'BACK' }
  | { type: 'ESCALATION_SUBMITTED'; hasTalkdown: boolean; deferred?: boolean }
  | { type: 'TALKDOWN_SENT'; totalSteps: number }
  | { type: 'TALKDOWN_FINISHED' }
  | { type: 'QUICK_RESOLVE_NEEDS_DETAIL'; action?: QuickResolveActionItem }
  | { type: 'RESOLVE_CANCELLED' };

export function defaultTabForStep(step: SOPV2Step): SOPTabId {
  switch (step) {
    case SOPV2Step.LIVE_TALKDOWN:
      return SOPTabId.TALKDOWN_NOTES;
    case SOPV2Step.ESCALATE_DESCRIPTION:
    case SOPV2Step.ESCALATION_ACTIONS:
      return SOPTabId.ESCALATION_PROTOCOL;
    default:
      return SOPTabId.SITUATIONS;
  }
}

function sopV2Reducer(state: SOPV2State, action: SOPV2Action): SOPV2State {
  switch (action.type) {
    case 'ALREADY_ESCALATED':
      if (state.pendingEscalation) {
        return {
          ...state,
          pendingEscalation: undefined,
          escalationCommitted: true,
          step: state.pendingEscalation.hasTalkdown
            ? SOPV2Step.LIVE_TALKDOWN
            : SOPV2Step.ESCALATION_ACTIONS,
        };
      }
      if (
        state.step !== SOPV2Step.OVERVIEW &&
        state.step !== SOPV2Step.ESCALATE_DESCRIPTION
      ) {
        return state;
      }
      return {
        ...state,
        step: SOPV2Step.ESCALATION_ACTIONS,
        escalationCommitted: true,
      };

    case 'ESCALATION_GONE':
      return {
        ...state,
        step: SOPV2Step.OVERVIEW,
        escalationCommitted: false,
        pendingEscalation: undefined,
        talkdownStepsDone: 0,
      };

    case 'ESCALATE_CLICKED':
      return { ...state, step: SOPV2Step.ESCALATE_DESCRIPTION };

    case 'SITUATION_SELECTED':
      return {
        ...state,
        step: SOPV2Step.ESCALATE_DESCRIPTION,
        selectedSituation: action.situation,
      };

    case 'BACK':
      if (state.escalationCommitted) {
        return state;
      }
      return {
        ...state,
        step: SOPV2Step.OVERVIEW,
        selectedSituation: undefined,
        pendingEscalation: undefined,
      };

    case 'ESCALATION_SUBMITTED':
      if (action.deferred) {
        return {
          ...state,
          pendingEscalation: { hasTalkdown: action.hasTalkdown },
        };
      }
      return {
        ...state,
        escalationCommitted: true,
        step: action.hasTalkdown
          ? SOPV2Step.LIVE_TALKDOWN
          : SOPV2Step.ESCALATION_ACTIONS,
      };

    case 'TALKDOWN_SENT':
      return {
        ...state,
        talkdownStepsDone: Math.min(
          state.talkdownStepsDone + 1,
          action.totalSteps
        ),
      };

    case 'TALKDOWN_FINISHED':
      return { ...state, step: SOPV2Step.ESCALATION_ACTIONS };

    case 'QUICK_RESOLVE_NEEDS_DETAIL':
      return {
        ...state,
        step: SOPV2Step.RESOLVE,
        selectedQuickResolveAction: action.action,
      };

    case 'RESOLVE_CANCELLED':
      return {
        ...state,
        step: state.escalationCommitted
          ? SOPV2Step.ESCALATION_ACTIONS
          : SOPV2Step.OVERVIEW,
        selectedQuickResolveAction: undefined,
      };

    default:
      return state;
  }
}

function isStale(
  restored: PersistedState | undefined,
  isAlreadyEscalated: boolean
): boolean {
  return !!restored?.escalationCommitted && !isAlreadyEscalated;
}

export function readPersistedStep(
  alarmGroupId: string,
  isAlreadyEscalated: boolean
): SOPV2Step | undefined {
  const restored = readPersisted(alarmGroupId);
  return isStale(restored, isAlreadyEscalated) ? undefined : restored?.step;
}

export function useSOPV2Machine(
  isAlreadyEscalated: boolean,
  alarmGroupId: string,
  isResolved = false
) {
  const [state, dispatch] = useReducer(
    sopV2Reducer,
    { isAlreadyEscalated, alarmGroupId },
    ({
      isAlreadyEscalated: alreadyEscalated,
      alarmGroupId: id,
    }): SOPV2State => {
      const restored = readPersisted(id);
      if (restored && !isStale(restored, alreadyEscalated)) {
        return {
          step: restored.step,
          escalationCommitted: restored.escalationCommitted,
          selectedSituation: restored.selectedSituation,
          talkdownStepsDone: restored.talkdownStepsDone ?? 0,
        };
      }
      return {
        step: alreadyEscalated
          ? SOPV2Step.ESCALATION_ACTIONS
          : SOPV2Step.OVERVIEW,
        escalationCommitted: alreadyEscalated,
        talkdownStepsDone: 0,
      };
    }
  );

  useEffect(() => {
    if (isResolved) {
      clearPersisted(alarmGroupId);
      return;
    }
    writePersisted(alarmGroupId, state);
  }, [alarmGroupId, state, isResolved]);

  return [state, dispatch] as const;
}

const STORAGE_PREFIX = 'sop-v2-step:';

interface PersistedState {
  step: SOPV2Step;
  escalationCommitted: boolean;
  selectedSituation?: Situation;
  talkdownStepsDone?: number;
}

function isStep(value: string): value is SOPV2Step {
  const steps: string[] = Object.values(SOPV2Step);
  return steps.includes(value);
}

function isPersisted(
  value: Partial<PersistedState> | null
): value is PersistedState {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const { step, escalationCommitted, talkdownStepsDone, selectedSituation } =
    value;
  return (
    typeof step === 'string' &&
    isStep(step) &&
    typeof escalationCommitted === 'boolean' &&
    (selectedSituation === undefined ||
      typeof selectedSituation.label === 'string') &&
    (talkdownStepsDone === undefined ||
      (typeof talkdownStepsDone === 'number' &&
        Number.isInteger(talkdownStepsDone) &&
        talkdownStepsDone >= 0))
  );
}

function readPersisted(alarmGroupId: string): PersistedState | undefined {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_PREFIX + alarmGroupId);
    if (!raw) {
      return undefined;
    }
    const parsed = JSON.parse(raw);
    if (isPersisted(parsed)) {
      return parsed;
    }
    return undefined;
  } catch {
    return undefined;
  }
}

function clearPersisted(alarmGroupId: string) {
  try {
    window.sessionStorage.removeItem(STORAGE_PREFIX + alarmGroupId);
  } catch {
    return;
  }
}

function writePersisted(alarmGroupId: string, state: SOPV2State) {
  const step =
    state.step === SOPV2Step.RESOLVE
      ? state.escalationCommitted
        ? SOPV2Step.ESCALATION_ACTIONS
        : SOPV2Step.OVERVIEW
      : state.step;

  const payload: PersistedState = {
    step,
    escalationCommitted: state.escalationCommitted,
    selectedSituation: state.selectedSituation,
    talkdownStepsDone: state.talkdownStepsDone,
  };

  try {
    window.sessionStorage.setItem(
      STORAGE_PREFIX + alarmGroupId,
      JSON.stringify(payload)
    );
  } catch {
    return;
  }
}

export function useSyncTabToStep(step: SOPV2Step) {
  const { setActiveTab, setCompressed } = useSOPCardState();
  const previousStep = useRef(step);

  useEffect(() => {
    if (previousStep.current !== step) {
      previousStep.current = step;
      setActiveTab(defaultTabForStep(step));
      setCompressed(step === SOPV2Step.ESCALATE_DESCRIPTION);
    }
  }, [step, setActiveTab, setCompressed]);
}

export default useSOPV2Machine;
